/**
 * `tarii.signup_intent` — the create-or-join intent stash (#1720).
 *
 * A person arriving at sign-up sometimes already knows which of the two
 * doors they want: **create** a team of their own, or **join** one that
 * already exists. When they tell us before authenticating, we carry the
 * answer across the Clerk hop and skip the post-auth chooser (`/start`).
 * When they don't, the chooser asks — the one thing that must never
 * happen again is the silent default into founding a tenant, which is
 * what a zero-team account got before this issue.
 *
 * **Contract mirrors `consumePendingInviteRedirect()` in `App.tsx`**
 * deliberately — same storage medium, same one-shot read, same
 * swallow-everything posture:
 *
 *   - `sessionStorage` (not a search-param) because the value has to
 *     survive the whole sign-up → email-verify → sign-in choreography,
 *     which a query string does not.
 *   - Reads CLEAR the key, always, even when the stored blob is
 *     corrupt — a bad value must never pin someone in a redirect loop.
 *   - Every storage access is wrapped: private-mode Safari throws on
 *     `sessionStorage` access, and an intent hint is never worth an
 *     exception on the auth path.
 *
 * **Precedence** (enforced by where the readers sit in the gate chain,
 * not by this module): `roster.invite_pending` ALWAYS wins — a person
 * who followed an invite link is going to that team's accept flow no
 * matter what they clicked earlier. `RequireAuthOrLanding` consumes the
 * invite stash and navigates away before `<HomeEntryGate/>` — the only
 * caller of `consumeSignupIntent()` — ever renders. Intent is the
 * second-place hint; no intent means the chooser.
 *
 * (The consumer is `useZeroTeamStartTarget`, called from
 * `<HomeEntryGate/>` — see both in `App.tsx`.)
 *
 * The stash is harmless in the dev-shim/demo runtime: nothing writes it
 * there (`/sign-up` bounces to `/sign-in` without a Clerk key), and a
 * stale value from an earlier Clerk build is consumed once and dropped.
 */

/** The two doors. `create` → `/onboarding`; `join` → `/join`. */
export type SignupIntent = "create" | "join";

export const SIGNUP_INTENT_KEY = "tarii.signup_intent";

function isSignupIntent(value: unknown): value is SignupIntent {
  return value === "create" || value === "join";
}

/**
 * Record which door the person picked, before they authenticate.
 * Silently no-ops when storage is unavailable — the chooser is the
 * fallback and it is a perfectly good outcome.
 */
export function stashSignupIntent(intent: SignupIntent): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SIGNUP_INTENT_KEY, intent);
  } catch {
    // Private-mode Safari etc. — graceful no-op.
  }
}

/**
 * Read WITHOUT clearing. For surfaces that only need to know whether
 * the question has already been answered — the `/sign-up` intent strip
 * hides itself when a door on `/start` was already taken, so the
 * person is never asked twice. Never use this for routing decisions;
 * routing consumes.
 */
export function peekSignupIntent(): SignupIntent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_INTENT_KEY);
    return isSignupIntent(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Read + clear the intent. One-shot: a second call in the same session
 * returns `null`, so a person who lands on the chooser once is not
 * re-routed by a ghost value on their next visit to `/`.
 */
export function consumeSignupIntent(): SignupIntent | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(SIGNUP_INTENT_KEY);
  } catch {
    return null;
  }
  try {
    // Clear regardless of validation outcome — same reasoning as the
    // invite stash: a corrupt value must not survive to bounce the
    // next render too.
    window.sessionStorage.removeItem(SIGNUP_INTENT_KEY);
  } catch {
    /* no-op */
  }
  return isSignupIntent(raw) ? raw : null;
}
